import { useForm } from '@tanstack/react-form';
import { AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import * as React from 'react';

import type { ParameterView } from '@/api/parameters-client';
import { useCampaignActivity, useUpdateParameter } from '@/api/use-parameters';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  CAMPAIGN_IMPACT_WARNING,
  formatValue,
  isImmediatePropagationParameter,
  isSensitiveParameter,
  parseInputValue,
  rangeHint,
  REINFORCED_PROPAGATION_WARNING,
} from '@/lib/param-format';
import { SensitiveConfirmDialog } from '@/routes/parameters/sensitive-confirm-dialog';
import { TemplatePreview } from '@/routes/parameters/template-preview';

// =============================================================================
// parameter-edit-form.tsx — Édition d'un paramètre (AdminFull, écran A-24).
// =============================================================================
// TanStack Form (un champ) + mutation PATCH (TanStack Query). L'UI affiche un
// INDICE de plage (rangeHint) — guide UX, PAS la règle. L'AUTORITÉ de validation
// est l'API ([S2-TRANCHÉ #4]) : la valeur hors plage est rejetée serveur et son
// message est affiché tel quel (role="alert"). PAS de workflow 2 étapes (V1.5).
//
// État : in-memory (TanStack Form + React), aucun localStorage (RULE_REF_006).

export interface ParameterEditFormProps {
  readonly parameter: ParameterView;
}

export function ParameterEditForm({
  parameter,
}: ParameterEditFormProps): React.JSX.Element {
  const update = useUpdateParameter();
  // Erreur de coercition de saisie (type), distincte de l'erreur métier serveur.
  const [parseError, setParseError] = React.useState<string | null>(null);
  const sensitive = isSensitiveParameter(parameter);
  // V28-IMPACT-BANNER (§2.8 l.1854) — le bandeau d'impact existe SI des campagnes sont EN COURS,
  // plus un statut « sensible » statique. FAIL-CLOSED INVERSÉ (piège cadrage D5) : tant que le
  // signal n'est PAS un {inProgressCount:0, degraded:false} confirmé (chargement, erreur réseau
  // front, OU degraded:true renvoyé par l'API), on AFFICHE le bandeau — on avertit quand on ne
  // sait pas, jamais l'inverse (qui masquerait silencieusement l'avertissement).
  const campaignActivity = useCampaignActivity();
  const activity = campaignActivity.data;
  const showImpactBanner =
    activity === undefined || activity.inProgressCount > 0 || activity.degraded;
  // §2.8 l.1934 — cas dérogatoire « propagation immédiate » : avertissement RENFORCÉ, en PLUS du
  // bandeau générique (pas à sa place), quand le paramètre édité est de cette catégorie.
  const immediatePropagation = isImmediatePropagationParameter(parameter);
  // Type Chaine (TEXTE libre) : édition multi-lignes (<Textarea>) + prévisualisation
  // avant enregistrement (DBU §2.8 l.1926). Les autres types restent en <Input>.
  const isChaine = parameter.paramType === 'Chaine';
  const [showPreview, setShowPreview] = React.useState(false);
  // Modification en attente de confirmation (paramètre sensible) : la valeur est
  // parsée et retenue jusqu'à la confirmation textuelle, puis envoyée en PATCH.
  const [pending, setPending] = React.useState<{
    readonly value: number | boolean | string;
    readonly reason?: string;
  } | null>(null);

  const applyUpdate = React.useCallback(
    (input: {
      readonly value: number | boolean | string;
      readonly reason?: string;
    }) => {
      update.mutate({
        paramKey: parameter.paramKey,
        input: {
          value: input.value,
          ...(input.reason !== undefined ? { reason: input.reason } : {}),
        },
      });
    },
    [update, parameter.paramKey],
  );

  const form = useForm({
    defaultValues: {
      value: formatValue(parameter.value),
      reason: '',
    },
    onSubmit: ({ value }) => {
      setParseError(null);
      const parsed = parseInputValue(parameter.paramType, value.value);
      if (!parsed.ok) {
        // Coercition de type impossible : on n'envoie pas un type absurde au
        // serveur. Ce n'est PAS la règle de plage (autorité serveur), juste un
        // garde-fou de saisie pour un meilleur retour immédiat.
        setParseError(parsed.reason);
        return;
      }
      const reason = value.reason.trim();
      const input = {
        value: parsed.value,
        ...(reason.length > 0 ? { reason } : {}),
      };
      if (sensitive) {
        // §2.8 l.1936 — confirmation TEXTUELLE avant tout changement sensible.
        setPending(input);
        return;
      }
      applyUpdate(input);
    },
  });

  const hintId = `hint-${parameter.paramKey}`;
  const errorId = `error-${parameter.paramKey}`;
  const hasError = parseError !== null || update.isError;

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        event.stopPropagation();
        void form.handleSubmit();
      }}
      className="space-y-4"
      aria-describedby={hintId}
    >
      {/* §2.8 l.1854 — bandeau CONDITIONNEL d'impact campagnes : existe SI des campagnes sont EN
          COURS (read-model useCampaignActivity), plus un statut « sensible » statique. FAIL-CLOSED
          INVERSÉ (D5) : chargement/erreur/degraded ⇒ affiché (voir showImpactBanner ci-dessus).
          `role="note"` = information contextuelle a11y. */}
      {showImpactBanner ? (
        <div
          role="note"
          aria-label="Avertissement d'impact sur les campagnes en cours"
          className="flex items-start gap-3 rounded-md border border-accent-foreground/30 bg-accent p-4 text-accent-foreground"
        >
          <AlertTriangle
            className="mt-0.5 h-5 w-5 shrink-0"
            aria-hidden="true"
          />
          <p className="text-sm">{CAMPAIGN_IMPACT_WARNING}</p>
        </div>
      ) : null}

      {/* §2.8 l.1934 — cas dérogatoire « propagation immédiate » : avertissement RENFORCÉ, DISTINCT
          du bandeau générique ci-dessus (élément séparé, texte et rôle propres). AFFICHAGE seul —
          la propagation métier réelle (rupture du snapshot par campagne) est HORS LOT. */}
      {showImpactBanner && immediatePropagation ? (
        <div
          role="alert"
          aria-label="Avertissement renforcé : propagation immédiate aux campagnes en cours"
          className="flex items-start gap-3 rounded-md border border-destructive/40 bg-destructive/10 p-4"
        >
          <AlertTriangle
            className="mt-0.5 h-5 w-5 shrink-0 text-destructive"
            aria-hidden="true"
          />
          <p className="text-sm font-medium text-destructive">
            {REINFORCED_PROPAGATION_WARNING}
          </p>
        </div>
      ) : null}

      <div className="space-y-2">
        <Label htmlFor="param-value">Nouvelle valeur</Label>
        <form.Field name="value">
          {(field) =>
            isChaine ? (
              <div className="space-y-2">
                <Textarea
                  id="param-value"
                  name={field.name}
                  rows={10}
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  aria-invalid={hasError}
                  aria-describedby={hasError ? `${hintId} ${errorId}` : hintId}
                />
                {/* DBU §2.8 l.1926 — prévisualisation AVANT enregistrement. Le
                    contenu est rendu comme TEXTE inerte (garde XSS, cf.
                    TemplatePreview). Ne déclenche AUCUN PATCH. */}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  aria-expanded={showPreview}
                  onClick={() => setShowPreview((v) => !v)}
                >
                  {showPreview
                    ? 'Masquer la prévisualisation'
                    : 'Prévisualiser'}
                </Button>
                {showPreview ? (
                  <TemplatePreview content={field.state.value} />
                ) : null}
              </div>
            ) : (
              <Input
                id="param-value"
                name={field.name}
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                aria-invalid={hasError}
                aria-describedby={hasError ? `${hintId} ${errorId}` : hintId}
              />
            )
          }
        </form.Field>
        <p id={hintId} className="text-sm text-muted-foreground">
          {rangeHint(parameter.paramType, parameter.validRange)} La valeur est
          validée par le serveur.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="param-reason">Motif (optionnel)</Label>
        <form.Field name="reason">
          {(field) => (
            <Input
              id="param-reason"
              name={field.name}
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              placeholder="Ex. : ajustement décidé en comité"
            />
          )}
        </form.Field>
      </div>

      <div aria-live="polite" aria-atomic="true">
        {hasError ? (
          <div
            role="alert"
            id={errorId}
            className="flex items-start gap-3 rounded-md border border-destructive/40 bg-destructive/10 p-4"
          >
            <XCircle
              className="mt-0.5 h-5 w-5 shrink-0 text-destructive"
              aria-hidden="true"
            />
            <div className="space-y-1">
              <p className="font-medium text-destructive">Valeur refusée</p>
              <p className="text-sm text-foreground">
                {parseError ?? update.error?.message}
              </p>
            </div>
          </div>
        ) : null}

        {update.isSuccess && !hasError ? (
          <div
            role="status"
            className="flex items-start gap-3 rounded-md border border-success/40 bg-success/10 p-4"
          >
            <CheckCircle2
              className="mt-0.5 h-5 w-5 shrink-0 text-success"
              aria-hidden="true"
            />
            <p className="text-sm font-medium text-success">
              Paramètre mis à jour.
            </p>
          </div>
        ) : null}
      </div>

      <Button type="submit" disabled={update.isPending}>
        {update.isPending ? 'Enregistrement…' : 'Enregistrer'}
      </Button>

      <SensitiveConfirmDialog
        open={pending !== null}
        paramKey={parameter.paramKey}
        sensitive={sensitive}
        title="Confirmer la modification"
        description={`Vous allez modifier « ${parameter.paramKey} ». Cette confirmation est requise pour un paramètre sensible.`}
        confirmLabel="Appliquer la modification"
        onCancel={() => setPending(null)}
        onConfirm={() => {
          if (pending !== null) {
            applyUpdate(pending);
          }
          setPending(null);
        }}
      />
    </form>
  );
}
